import React, { useRef, useState } from "react";
import { normalizeSpokenMeasure } from "../lib/spokenNumbers";
import { api } from "../api";
import { useApp } from "../context/AppContext";
import { getMic, releaseMic } from "../lib/mic";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";

/**
 * Dictation button: records audio, transcribes it with Whisper and returns the text.
 * Reusable anywhere a text answer can also be spoken (e.g. issue resolution notes).
 */
export function VoiceInputButton({ onTranscript, className = "" }) {
  const { t } = useApp();
  const [state, setState] = useState("idle"); // idle | recording | busy
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await getMic();
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        releaseMic(60000); // keep warm for a minute for follow-up dictations
        setState("busy");
        try {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          const fd = new FormData();
          fd.append("file", blob, "dictation.webm");
          const res = await api.post("/transcribe", fd); // authenticated (AI consent gate)
          const text = normalizeSpokenMeasure((res.data.transcript || "").trim());
          if (text) onTranscript(text);
          else toast.info(t("no_speech_detected"));
        } catch (e) {
          toast.error("Failed");
        } finally {
          setState("idle");
        }
      };
      mr.start();
      recRef.current = mr;
      setState("recording");
    } catch (e) {
      toast.error(t("mic_denied"));
    }
  };

  const stop = () => {
    try { recRef.current?.stop(); } catch (e) { /* ignore */ }
  };

  if (state === "busy") {
    return (
      <button type="button" disabled data-testid="voice-input-busy" className={`h-9 w-9 rounded-full bg-muted flex items-center justify-center ${className}`}>
        <Loader2 className="h-4 w-4 animate-spin text-primary" />
      </button>
    );
  }
  if (state === "recording") {
    return (
      <button
        type="button"
        data-testid="voice-input-stop"
        onClick={stop}
        className={`h-9 w-9 rounded-full bg-destructive text-white flex items-center justify-center animate-pulse ${className}`}
        title={t("stop")}
      >
        <Square className="h-4 w-4" />
      </button>
    );
  }
  return (
    <button
      type="button"
      data-testid="voice-input-start"
      onClick={start}
      className={`h-9 w-9 rounded-full bg-primary/10 text-primary hover:bg-primary/20 flex items-center justify-center transition-colors ${className}`}
      title={t("dictate")}
    >
      <Mic className="h-4 w-4" />
    </button>
  );
}
